// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { RgbaStringColorPicker } from "react-colorful";

import { colord } from "colord";
import { useField } from "formik";
import debounce from "lodash-es/debounce";

import { Header, Button } from "components/elements";
import { Input } from "components/form/elements";
import { GrabsonIcon } from "components/layout";
import { PopupOnClick, Tooltip } from "components/popups";
import { popupAlignmentType } from "components/popups/Popup/types";
import transparentSvg from "images/transparent.svg";
import { trans } from "src/translations";
import { getFieldTestId } from "utils/formik";

import { createMixedValidator } from ".";
import styles from "./ColorField.scss";
import { FieldProps } from "./propTypes";

const debounceDelay = 100;

const getBackgroundColor = (value) =>
  value
    ? `linear-gradient(to right, ${value}, ${value}), url(${transparentSvg})`
    : `url(${transparentSvg})`;

const renderPresets = (field, helpers, presets, testId) =>
  presets && (
    <>
      <Header
        type="h6"
        color="tertiary"
        uppercase={true}
        className={styles.colorPresetHeader}
      >
        {trans.COLOR_FIELD_PRESETS_HEADER()}
      </Header>
      <ul className={styles.colorPresetList}>
        {presets.map((color, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${field.name}-${color}-${index}`}>
            <Button
              className={styles.colorPresetButton}
              type="plain"
              dataTestId={`${testId}-preset-button`}
              onClick={() => helpers.setValue(color.value)}
            >
              <Tooltip
                alignment="top"
                title={color.label}
                classNamePopup={styles.colorPresetTooltip}
              >
                <span
                  className={styles.colorPresetIcon}
                  style={{
                    background: getBackgroundColor(color.value),
                  }}
                />
              </Tooltip>
            </Button>
          </li>
        ))}
      </ul>
    </>
  );

const renderTrigger = (props, field, meta, testId) => (
  <Input
    {...meta}
    {...props}
    className={styles.colorTrigger}
    props={{
      ...field,
      autoComplete: "off",
    }}
    required={field.required}
    dataTestId={testId}
  >
    <div
      className={styles.colorPreview}
      style={{
        background: getBackgroundColor(field.value),
      }}
    />
    <GrabsonIcon className={styles.colorIcon} name="edit" size="xl" />
  </Input>
);

const renderPopup = (field, helpers, presets, testId) => (
  <div className={styles.colorPopup} data-test-id={testId}>
    <RgbaStringColorPicker
      className={styles.colorPicker}
      color={colord(field.value).toRgbString()}
      onChange={debounce(
        (value) => helpers.setValue(colord(value).toHex()),
        debounceDelay
      )}
    />
    {renderPresets(field, helpers, presets, `${testId}-presets`)}
  </div>
);

export const ColorField = (props) => {
  const {
    name,
    popupPosition,
    presets,
    required = true,
    validate,
    dataTestId,
  } = props;

  const [field, meta, helpers] = useField({
    ...props,
    popupPosition,
    required,
    type: "text",
    validate: createMixedValidator(name, required, validate),
  });
  const testId = dataTestId || getFieldTestId(name);

  return (
    <PopupOnClick
      alignment={popupPosition}
      className={styles.colorWrapper}
      classNamePopupWrapper={styles.colorPopupWrapper}
      dataTestId={testId}
      renderPopup={() =>
        renderPopup(field, helpers, presets, `${testId}-popup`)
      }
      renderTrigger={() =>
        renderTrigger(props, field, meta, `${testId}-trigger`)
      }
    />
  );
};

ColorField.propTypes = {
  popupPosition: popupAlignmentType,
  ...FieldProps,
};
